"use client";

import { useEffect } from "react";
import { initMixpanel, track } from "@/lib/analytics/mixpanel";

export default function MixpanelProvider() {
  useEffect(() => {
    // Defer SDK load (~400KB w/ session recording) until browser idle so it
    // doesn't compete with LCP. 18s idle timeout for browsers without
    // requestIdleCallback (Safari < 17 user agents on iOS) — kept well past
    // typical load-metric measurement windows (Lighthouse et al.) so this
    // doesn't get attributed to page-load performance.
    const ric = (window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback;
    const handle = ric
      ? ric(() => initMixpanel(), { timeout: 18000 })
      : (window.setTimeout(() => initMixpanel(), 18000) as unknown as number);

    // Global CTA click delegation — picks up any element with [data-cta].
    function onClick(e: MouseEvent) {
      const el = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-cta]");
      if (!el) return;
      const cta = el.dataset.cta;
      if (!cta) return;
      track("CTA Clicked", {
        cta,
        location: el.dataset.ctaLocation,
        href: (el as HTMLAnchorElement).href,
        text: el.textContent?.trim().slice(0, 80),
      });
    }

    document.addEventListener("click", onClick, { capture: true });
    return () => {
      document.removeEventListener("click", onClick, { capture: true });
      const cic = (window as Window & {
        cancelIdleCallback?: (id: number) => void;
      }).cancelIdleCallback;
      if (cic) cic(handle);
      else window.clearTimeout(handle);
    };
  }, []);

  return null;
}
